import { arrayMoveImmutable } from 'array-move'

import { ILWFCColumn } from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'

export enum ColumnsConfigSection {
  Freezed = 'freezed',
  Hidden = 'hidden',
  None = 'none',
}

interface IColumnsMenuSections {
  freezedItems: ILWFCColumn[]
  hiddenItems: ILWFCColumn[]
  shownItems: ILWFCColumn[]
}

interface IBuildColumnsMenuSectionsArgs {
  columns?: ILWFCColumn[]
  searchKey: string
  excludedFromSort?: string[]
}

interface IMoveColumnsMenuItemArgs extends IBuildColumnsMenuSectionsArgs {
  oldIndex: number
  newIndex: number
  section: ColumnsConfigSection
}

const matchesSearch = (column: ILWFCColumn, searchKey: string): boolean =>
  !column.label ||
  column.label.toLowerCase().includes(searchKey.trim().toLowerCase())

const isSortableColumn = (
  column: ILWFCColumn,
  excludedFromSort: Set<string>,
): boolean => !excludedFromSort.has(column.dataKey)

export const buildColumnsMenuSections = ({
  columns = [],
  searchKey,
  excludedFromSort = [],
}: IBuildColumnsMenuSectionsArgs): IColumnsMenuSections => {
  const excludedKeys = new Set(excludedFromSort)

  return columns
    .filter(column => matchesSearch(column, searchKey))
    .filter(column => isSortableColumn(column, excludedKeys))
    .reduce<IColumnsMenuSections>(
      (sections, column) => {
        if (column.isHidden) {
          sections.hiddenItems.push(column)
        } else if (column.isFixed) {
          sections.freezedItems.push(column)
        } else {
          sections.shownItems.push(column)
        }

        return sections
      },
      { freezedItems: [], hiddenItems: [], shownItems: [] },
    )
}

const getRenderedSortableColumns = (
  sections: IColumnsMenuSections,
): ILWFCColumn[] => [
  ...sections.freezedItems,
  ...sections.shownItems,
  ...sections.hiddenItems,
]

const normalizeSortableColumns = (columns: ILWFCColumn[]): ILWFCColumn[] => [
  ...columns.filter(column => column.isFixed),
  ...columns.filter(column => !column.isFixed && !column.isHidden),
  ...columns.filter(column => column.isHidden),
]

const applySectionState = (
  column: ILWFCColumn,
  section: ColumnsConfigSection,
): ILWFCColumn => {
  switch (section) {
    case ColumnsConfigSection.Freezed:
      return { ...column, isFixed: true, isHidden: false }
    case ColumnsConfigSection.Hidden:
      return { ...column, isFixed: false, isHidden: true }
    case ColumnsConfigSection.None:
    default:
      return { ...column, isFixed: false, isHidden: false }
  }
}

export const moveColumnsMenuItem = ({
  columns = [],
  searchKey,
  excludedFromSort = [],
  oldIndex,
  newIndex,
  section,
}: IMoveColumnsMenuItemArgs): ILWFCColumn[] => {
  const excludedKeys = new Set(excludedFromSort)
  const fullSortableColumns = getRenderedSortableColumns(
    buildColumnsMenuSections({
      columns,
      searchKey: '',
      excludedFromSort,
    }),
  )
  const visibleSortableColumns = getRenderedSortableColumns(
    buildColumnsMenuSections({
      columns,
      searchKey,
      excludedFromSort,
    }),
  )
  const movedColumn = visibleSortableColumns[oldIndex]

  if (!movedColumn) {
    return columns
  }

  const reorderedVisibleColumns = arrayMoveImmutable(
    visibleSortableColumns,
    oldIndex,
    newIndex,
  ).map(column =>
    column.dataKey === movedColumn.dataKey
      ? applySectionState(column, section)
      : column,
  )

  const visibleKeys = new Set(
    visibleSortableColumns.map(column => column.dataKey),
  )
  let visibleIndex = 0
  const reorderedSortableColumns = normalizeSortableColumns(
    fullSortableColumns.map(column => {
      if (!visibleKeys.has(column.dataKey)) {
        return column
      }

      const nextColumn = reorderedVisibleColumns[visibleIndex]
      visibleIndex += 1
      return nextColumn
    }),
  )

  let sortableIndex = 0
  return columns.map(column => {
    if (!isSortableColumn(column, excludedKeys)) {
      return column
    }

    const nextColumn = reorderedSortableColumns[sortableIndex]
    sortableIndex += 1
    return nextColumn
  })
}
